"use client";
import { FlexBetween, FlexBetweenCol, FlexCol } from "@/components";
import { cn } from "@/lib";
import CreditCard from "./creditCard";
import CustomButton from "../ui/customButton";
import { useForm } from "react-hook-form";
import { IInquiriesComponentProps, TCreditCard } from "@/interfaces";
import { zodResolver } from "@hookform/resolvers/zod";
import { creditCardSchema } from "@/validations";
import { useSession } from "next-auth/react";
import { useCardToIbanInquiryMutation } from "@/hooks/useInquiry";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import { useModalStore } from "@/store";
import { landingQuickAccessNavigation } from "@/constants";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import { useState } from "react";
import PaymentModalContainer from "@/components/Payment/paymentModalContainer";
import { globalRawServicesApiUnification } from "@/constants/globalRawServicesData/globalRawServicesApiUnification";
import { ICardToIbanInquiryMutationPayload } from "@/interfaces/inquiries/bank";

const CardToIban = ({
  openPaymentModal,
  isLoggedIn,
  navigateToLogin,
  isOpen,
  onClose,
}: IInquiriesComponentProps) => {
  const t = useTranslations();
  const { closeModal } = useModalStore();
  const { data: session } = useSession();
  const router = useRouter();
  const {
    control,
    handleSubmit,
    formState: { errors: formError },
    getValues,
    setValue,
  } = useForm<TCreditCard>({
    resolver: zodResolver(creditCardSchema),
    defaultValues: { input1: "", input2: "", input3: "", input4: "" },
  });
  const {
    isPending: isSubmittingCardToIban,
    mutate: cardToIbanInquiryFn,
    error: cardToIbanInquiryError,
    reset: resetCardToIbanInquiry,
  } = useCardToIbanInquiryMutation(session?.user.token);
  const [packageId, setPackageId] = useState<string | null>(null);
  const handlePackageSelect = (packageId: string) => {
    setPackageId(packageId);
  };
  const cardToIbanInquiryDetails = landingQuickAccessNavigation.find(
    (item) => item.key === globalRawServicesData.Card_To_Iban.key,
  );

  const onSubmit = (data: any) => {
    if (!isLoggedIn) {
      navigateToLogin();
      return;
    }
    openPaymentModal();
  };

  const paymentSubmission = () => {
    const formData = getValues();
    const fullCard = Object.values(formData).join("");
    const payload: Readonly<ICardToIbanInquiryMutationPayload> = {
      number: fullCard,
      campaign: packageId || undefined,
    };
    cardToIbanInquiryFn(
      { payload },
      {
        onSuccess: (data) => {
          const purchase_id = data.data?.purchase_id;
          if (purchase_id) {
            router.push(
              `${routes.main.INQUIRY_DETAILS.primaryPath}/${data.data?.purchase_id}`,
            );
            closeModal();
          }
        },
      },
    );
  };

  return (
    <>
      <FlexBetween className="items-stretch gap-8 lg:gap-12">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cn("flex-center sm:basis-1/2")}
        >
          <FlexBetweenCol gap={4} className={cn("w-full xl:gap-8")}>
            <div>
              <CreditCard
                variant="credit"
                formError={formError}
                control={control}
                setValue={setValue}
              />
              {Object.keys(formError).length > 0 && (
                <p className="text-error-main mt-4 w-full text-xs">
                  {t("inquiries.cardToIban.cardError")}
                </p>
              )}
            </div>
            <CustomButton
              id="landing-card-to-iban-submit"
              type="submit"
              className="mt-2 w-max lg:mt-4"
              onClick={handleSubmit(onSubmit)}
            >
              {t("inquiries.cardToIban.submitButton")}
            </CustomButton>
          </FlexBetweenCol>
        </form>

        <FlexCol
          gap={2}
          className={cn("hidden basis-1/2 sm:flex lg:gap-4 xl:gap-6")}
        >
          <h2 className={cn("text-sm font-bold lg:text-base xl:text-xl")}>
            {t("inquiries.cardToIban.headerTitle")}
          </h2>
          <p
            className={cn(
              "text-custom-gray-200 text-justify text-xs leading-5 whitespace-pre-line lg:text-sm lg:leading-normal xl:text-base",
            )}
          >
            {t("inquiries.cardToIban.description")}
          </p>
        </FlexCol>
      </FlexBetween>
      {isOpen && (
        <PaymentModalContainer
          payBtnId="landing-payment-card-to-iban"
          submissionLoading={isSubmittingCardToIban}
          submissionError={cardToIbanInquiryError}
          paymentSubmission={paymentSubmission}
          isOpen={isOpen}
          onClose={onClose}
          showData="service"
          serviceId={globalRawServicesApiUnification.Card_To_Iban.apiId}
          selectedService={cardToIbanInquiryDetails}
          onErrorReset={resetCardToIbanInquiry}
          onPackageSelect={handlePackageSelect}
        />
      )}
    </>
  );
};

export default CardToIban;
